/**
 * @deprecated Prefer the renderer-first pipeline for new code. Use
 * @renderer/utils/pipeline/outputOrchestrator (processAndUploadStrip, processAndUploadGIF,
 * processAndUploadPhotos, processAndUploadSingleFile) and @renderer/utils/pipeline/uploadQueue
 * (uploadFile) with ProcessingContext. Custom routes and view/Record have been migrated.
 */
import S3Utils from './s3Utils'
import { useSubscriptionStore } from '@renderer/stores/subscriptionStore'
import { useCreditsStore } from '@renderer/stores/creditsStore'
import { UploadTask } from '@renderer/types/upload'
import { updateUploadProgress } from '@renderer/stores/uploadStore'
import { useConfigStore } from '@renderer/stores/configState'
import cogoToast from '@successtar/cogo-toast'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useAuthStore } from '@renderer/stores/authStore'
import { resolveGallerySessionUrlFromStore } from '@renderer/utils/galleryUrlUtils'

type ElectronBridge = {
  saveFile?: (
    sessionId: string,
    fileName: string,
    fileType: 'photos' | 'videos' | 'gifs',
    data: ArrayBuffer,
    metadata?: {
      width?: number
      height?: number
      duration?: number
      frameCount?: number
      mimeType?: string
    }
  ) => Promise<{ success: boolean; path?: string; resourceId?: string; error?: string }>
  getInternetStatus?: () => Promise<boolean>
  markMediaAsUploaded?: (
    sessionId: string,
    resourceId: string,
    resourceType: 'photo' | 'video' | 'gif',
    fileName: string
  ) => Promise<{ success: boolean; error?: string }>
}

/**
 * Check real network connectivity status
 * Uses window.api.getInternetStatus() if available (Electron), otherwise falls back to navigator.onLine
 */
async function checkNetworkStatus(): Promise<boolean> {
  if (typeof window === 'undefined') return true // Assume online in SSR
  const bridge = window as typeof window & { api?: ElectronBridge }
  if (bridge.api?.getInternetStatus) {
    return await bridge.api.getInternetStatus()
  }
  return navigator.onLine // Fallback to browser API
}

/**
 * Check if preload API (saveFile) is available
 * This indicates the main process has the offline pipeline capability
 */
function hasPreloadAPI(): boolean {
  if (typeof window === 'undefined') return false
  const bridge = window as typeof window & { api?: ElectronBridge }
  return Boolean(bridge.api?.saveFile)
}

/**
 * Calculate a simple hash for a file to detect duplicates
 * Uses file name + size + type + last modified time
 */
async function calculateFileHash(file: File): Promise<string> {
  const content = `${file.name}|${file.size}|${file.type}|${file.lastModified}`

  // Simple hash function (djb2 algorithm)
  let hash = 5381
  for (let i = 0; i < content.length; i++) {
    hash = (hash << 5) + hash + content.charCodeAt(i)
  }
  return hash.toString(36)
}

/**
 * Check if file has already been uploaded (deduplication)
 * Tracks uploaded file hashes in localStorage per session
 */
async function isFileAlreadyUploaded(sessionId: string, file: File): Promise<boolean> {
  const storageKey = `uploaded-files-${sessionId}`

  try {
    const fileHash = await calculateFileHash(file)
    const stored = localStorage.getItem(storageKey)
    const uploadedHashes = stored ? new Set<string>(JSON.parse(stored)) : new Set<string>()

    return uploadedHashes.has(fileHash)
  } catch (error) {
    console.error('[UPLOAD] Failed to check file deduplication:', error)
    return false // On error, allow upload (better to have duplicates than miss files)
  }
}

/**
 * Mark file as uploaded (for deduplication tracking)
 */
async function markFileAsUploaded(sessionId: string, file: File): Promise<void> {
  const storageKey = `uploaded-files-${sessionId}`

  try {
    const fileHash = await calculateFileHash(file)
    const stored = localStorage.getItem(storageKey)
    const uploadedHashes = stored ? new Set<string>(JSON.parse(stored)) : new Set<string>()

    uploadedHashes.add(fileHash)
    localStorage.setItem(storageKey, JSON.stringify(Array.from(uploadedHashes)))
  } catch (error) {
    console.error('[UPLOAD] Failed to mark file as uploaded:', error)
  }
}

// --- IndexedDB Helpers for File Persistence ---
const DB_NAME = 'upload-files-db'
const STORE_NAME = 'files'

// Add IndexedDB health check and recovery
let dbHealthStatus: 'healthy' | 'degraded' | 'failed' = 'healthy'
let dbFailureCount = 0
const MAX_DB_FAILURES = 3

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1)

    request.onerror = (): void => {
      dbFailureCount++
      if (dbFailureCount >= MAX_DB_FAILURES) {
        dbHealthStatus = 'failed'
        useSessionStateStore
          .getState()
          .logActivity(`IndexedDB marked as failed after ${dbFailureCount} consecutive failures`, {
            failureCount: dbFailureCount,
            maxFailures: MAX_DB_FAILURES,
            level: 'error'
          })
      } else {
        dbHealthStatus = 'degraded'
        useSessionStateStore
          .getState()
          .logActivity(`IndexedDB access failed (${dbFailureCount}/${MAX_DB_FAILURES})`, {
            failureCount: dbFailureCount,
            maxFailures: MAX_DB_FAILURES,
            level: 'warning'
          })
      }
      reject(new Error('Error opening IndexedDB'))
    }

    request.onsuccess = (): void => {
      // Reset failure count on success
      if (dbFailureCount > 0) {
        dbFailureCount = 0
        dbHealthStatus = 'healthy'
      }
      resolve(request.result)
    }

    request.onupgradeneeded = (event): void => {
      const db = (event.target as IDBOpenDBRequest).result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME)
      }
    }
  })
}

// Add IndexedDB health check function
async function checkDBHealth(): Promise<boolean> {
  try {
    if (dbHealthStatus === 'failed') {
      return false
    }

    // Try to open a test connection
    const db = await openDB()
    db.close()
    return true
  } catch {
    useSessionStateStore.getState().logActivity(`IndexedDB health check failed`, {
      level: 'warning'
    })
    return false
  }
}

// Enhanced save function with fallback logging
async function saveFileInDB(id: string, file: File): Promise<void> {
  if (dbHealthStatus === 'failed') {
    throw new Error('IndexedDB is marked as failed')
  }

  try {
    const db = await openDB()
    return new Promise((resolve, reject) => {
      try {
        const transaction = db.transaction(STORE_NAME, 'readwrite')
        const store = transaction.objectStore(STORE_NAME)
        const request = store.put(file, id)

        request.onsuccess = (): void => {
          resolve()
        }

        request.onerror = (): void => {
          const errorMsg = 'Error saving file to IndexedDB'
          useSessionStateStore.getState().logActivity(`IndexedDB save failed: ${errorMsg}`, {
            dbId: id,
            fileName: file.name,
            level: 'error'
          })
          reject(new Error(errorMsg))
        }
      } catch (error) {
        const errorMsg = 'Error saving file to IndexedDB'
        useSessionStateStore.getState().logActivity(`IndexedDB save exception: ${errorMsg}`, {
          dbId: id,
          fileName: file.name,
          error: error instanceof Error ? error.message : 'Unknown error',
          level: 'error'
        })
        reject(error)
      }
    })
  } catch (error) {
    // Mark DB as degraded on save failure
    if (dbHealthStatus === 'healthy') {
      dbHealthStatus = 'degraded'
      useSessionStateStore
        .getState()
        .logActivity(`IndexedDB marked as degraded due to save failure`, {
          level: 'warning'
        })
    }
    throw error
  }
}

// Enhanced load function with fallback
async function loadFileFromDB(id: string): Promise<File | null> {
  if (dbHealthStatus === 'failed') {
    useSessionStateStore
      .getState()
      .logActivity(`Skipping IndexedDB load for ${id}: DB marked as failed`, {
        dbId: id,
        level: 'warning'
      })
    return null
  }

  try {
    const db = await openDB()
    return new Promise((resolve, reject) => {
      try {
        const transaction = db.transaction(STORE_NAME, 'readonly')
        const store = transaction.objectStore(STORE_NAME)
        const request = store.get(id)

        request.onsuccess = (): void => {
          const file = request.result as File | null
          if (!file) {
            useSessionStateStore.getState().logActivity(`File not found in IndexedDB: ${id}`, {
              dbId: id,
              level: 'warning'
            })
          }
          resolve(file)
        }

        request.onerror = (): void => {
          const errorMsg = 'Error loading file from IndexedDB'
          useSessionStateStore.getState().logActivity(`IndexedDB load failed: ${errorMsg}`, {
            dbId: id,
            level: 'error'
          })
          reject(new Error(errorMsg))
        }
      } catch (error) {
        const errorMsg = 'Error loading file from IndexedDB'
        useSessionStateStore.getState().logActivity(`IndexedDB load exception: ${errorMsg}`, {
          dbId: id,
          error: error instanceof Error ? error.message : 'Unknown error',
          level: 'error'
        })
        reject(error)
      }
    })
  } catch {
    // Mark DB as degraded on load failure
    if (dbHealthStatus === 'healthy') {
      dbHealthStatus = 'degraded'
      useSessionStateStore
        .getState()
        .logActivity(`IndexedDB marked as degraded due to load failure`, {
          level: 'warning'
        })
    }
    return null
  }
}

// Enhanced delete function with fallback
async function deleteFileFromDB(id: string): Promise<void> {
  if (dbHealthStatus === 'failed') {
    useSessionStateStore
      .getState()
      .logActivity(`Skipping IndexedDB delete for ${id}: DB marked as failed`, {
        dbId: id,
        level: 'warning'
      })
    return
  }

  try {
    const db = await openDB()
    return new Promise((resolve, reject) => {
      try {
        const transaction = db.transaction(STORE_NAME, 'readwrite')
        const store = transaction.objectStore(STORE_NAME)
        const request = store.delete(id)

        request.onsuccess = (): void => {
          resolve()
        }

        request.onerror = (): void => {
          const errorMsg = 'Error deleting file from IndexedDB'
          useSessionStateStore.getState().logActivity(`IndexedDB delete failed: ${errorMsg}`, {
            dbId: id,
            level: 'error'
          })
          reject(new Error(errorMsg))
        }
      } catch (error) {
        const errorMsg = 'Error deleting file from IndexedDB'
        useSessionStateStore.getState().logActivity(`IndexedDB delete exception: ${errorMsg}`, {
          dbId: id,
          error: error instanceof Error ? error.message : 'Unknown error',
          level: 'error'
        })
        reject(error)
      }
    })
  } catch {
    // Mark DB as degraded on delete failure
    if (dbHealthStatus === 'healthy') {
      dbHealthStatus = 'degraded'
      useSessionStateStore
        .getState()
        .logActivity(`IndexedDB marked as degraded due to delete failure`, {
          level: 'warning'
        })
    }
    // Don't throw on delete failure - just log it
    useSessionStateStore.getState().logActivity(`Failed to delete file from IndexedDB: ${id}`, {
      dbId: id,
      level: 'warning'
    })
  }
}

// Add expiration calculation helper
const calculateExpirationDate = (subscriptionType: string | null): Date => {
  const now = new Date()
  switch (subscriptionType) {
    case 'enterprise':
      return new Date(now.setDate(now.getDate() + 365)) // 1 year
    case 'professional':
      return new Date(now.setDate(now.getDate() + 90)) // 90 days
    case 'starter':
      return new Date(now.setDate(now.getDate() + 30)) // 30 days
    case 'protrial':
    case 'free':
    default:
      return new Date(now.setDate(now.getDate() + 7)) // 7 days for free/protrial
  }
}

class UploadManager {
  private static instance: UploadManager
  private tasks: Map<string, UploadTask> = new Map()
  private maxRetries = 3

  static getInstance(): UploadManager {
    if (!this.instance) {
      this.instance = new UploadManager()
    }
    return this.instance
  }

  constructor() {
    this.loadTasks()
    window.addEventListener('beforeunload', () => this.saveTasks())
  }

  private updateAndNotify(): void {
    const tasksArray = Array.from(this.tasks.values())
    updateUploadProgress(tasksArray)
  }

  private saveTasks(): void {
    const tasksArray = Array.from(this.tasks.values()).map((task) => ({
      ...task,
      // Store file metadata instead of the file object
      file: null,
      fileData: task.file
        ? {
            name: task.file.name,
            size: task.file.size,
            type: task.file.type
          }
        : task.fileData
    }))
    localStorage.setItem('uploadTasks', JSON.stringify(tasksArray))

    this.updateAndNotify()
  }

  private loadTasks(): void {
    try {
      const savedTasks = localStorage.getItem('uploadTasks')
      if (savedTasks) {
        const tasksArray: UploadTask[] = JSON.parse(savedTasks)

        const restorationPromises = tasksArray.map(async (task) => {
          if (task.status !== 'completed') {
            const file = await loadFileFromDB(task.id)
            const restoredTask = { ...task, file }

            if (file) {
              if (restoredTask.status === 'uploading') {
                restoredTask.status = 'pending'
              }
              this.tasks.set(task.id, restoredTask)
              this.processTask(restoredTask)
            } else if (restoredTask.status !== 'failed') {
              // File not found in IndexedDB - check if we should mark as failed or continue
              if (dbHealthStatus === 'failed') {
                // IndexedDB is completely failed, mark task as failed
                restoredTask.status = 'failed'
                restoredTask.error =
                  'File not found in persistent storage due to IndexedDB failure.'
                useSessionStateStore
                  .getState()
                  .logActivity(`Task ${task.id} failed: IndexedDB is unavailable`, {
                    taskId: task.id,
                    type: task.type,
                    level: 'error',
                    dbHealthStatus
                  })
              } else {
                // IndexedDB might be temporarily unavailable, mark as failed but allow retry
                restoredTask.status = 'failed'
                restoredTask.error =
                  'File not found in persistent storage. Will retry on next session.'
                useSessionStateStore
                  .getState()
                  .logActivity(`Task ${task.id} failed: file not found in IndexedDB`, {
                    taskId: task.id,
                    type: task.type,
                    level: 'error',
                    dbHealthStatus
                  })
              }
              this.tasks.set(task.id, restoredTask)
            }
          } else {
            useSessionStateStore
              .getState()
              .logActivity(`Skipping completed task ${task.id} during restoration`, {
                taskId: task.id,
                type: task.type
              })
          }
        })
        Promise.all(restorationPromises).then(() => {
          useSessionStateStore.getState().logActivity(`Task restoration completed`, {
            restoredTaskCount: this.tasks.size,
            restoredTaskIds: Array.from(this.tasks.keys()),
            dbHealthStatus
          })
          this.updateAndNotify()
        })
      } else {
        this.updateAndNotify()
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error'
      console.error('Error loading tasks:', error)
      useSessionStateStore.getState().logActivity(`Error loading saved tasks: ${errorMsg}`, {
        error: errorMsg,
        level: 'error'
      })
      localStorage.removeItem('uploadTasks')
      this.updateAndNotify()
    }
  }

  async addTask(
    file: File,
    userId: string,
    projectId: string,
    sessionId: string,
    galleryId: string,
    type: 'image' | 'strip' | 'video' | 'gif' | 'recording' | 'live' = 'image',
    eventId: string | undefined = useConfigStore.getState().config?.eventGalleryEnabled
      ? useConfigStore.getState().config?.eventId
      : undefined,
    fallbackFile?: File
  ): Promise<{ taskId: string; promise: Promise<UploadTask['response']> }> {
    // Check for duplicate file upload
    const isDuplicate = await isFileAlreadyUploaded(sessionId, file)
    if (isDuplicate) {
      useSessionStateStore.getState().logActivity(`Skipping duplicate file upload: ${file.name}`, {
        fileName: file.name,
        type,
        sessionId,
        level: 'info'
      })
      // Return resolved promise with empty response - file already uploaded
      return {
        taskId: '',
        promise: Promise.resolve(null as unknown as UploadTask['response'])
      }
    }

    // Check real network status first
    const isOnline = await checkNetworkStatus()

    // If offline, prefer the offline pipeline via main process when available
    if (!isOnline) {
      const hasPreload = hasPreloadAPI()

      if (hasPreload) {
        useSessionStateStore.getState().logActivity(
          'Offline upload: using main-process offline pipeline',
          {
            fileName: file.name,
            type
          }
        )

        try {
          const { saveMediaOffline } = await import('@renderer/utils/offlineStorage')

          let fileType: 'photos' | 'videos' | 'gifs' = 'photos'
          if (type === 'gif') {
            fileType = 'gifs'
          } else if (type === 'recording' || type === 'video' || type === 'live') {
            fileType = 'videos'
          }

          let dimensions: { width: number; height: number } | null = null
          try {
            dimensions = await S3Utils.getImageDimensions(file)
          } catch (e) {
            console.warn('[uploadManager] Failed to get image dimensions:', e)
          }

          const metadata: { mimeType: string; width?: number; height?: number } = {
            mimeType: file.type
          }
          if (dimensions) {
            metadata.width = dimensions.width
            metadata.height = dimensions.height
          }

          await saveMediaOffline({
            sessionId,
            fileName: file.name,
            file,
            fileType,
            metadata
          })

          // Offline pipeline will handle upload when online
          return {
            taskId: '',
            promise: Promise.resolve(null as unknown as UploadTask['response'])
          }
        } catch (error) {
          useSessionStateStore.getState().logActivity('Offline pipeline save failed', {
            fileName: file.name,
            type,
            error: error instanceof Error ? error.message : 'Unknown error',
            level: 'error'
          })
          return {
            taskId: '',
            promise: Promise.reject(
              new Error(
                `Failed to save to offline pipeline: ${
                  error instanceof Error ? error.message : 'Unknown error'
                }`
              )
            )
          }
        }
      }

      // No preload API (e.g. web) - fall back to IndexedDB retry mechanism
      useSessionStateStore
        .getState()
        .logActivity(`Offline upload: preload API unavailable, using IndexedDB retry`, {
          fileName: file.name,
          type
        })
      // Fall through: task will be created and persisted for retry via IndexedDB.
    }

    const subscriptionType = useSubscriptionStore.getState().type?.toLowerCase() || 'free'
    const customPath = useConfigStore.getState().config?.pages?.welcome?.settings?.customPath

    // Allow video uploads for specific custom paths regardless of subscription
    const allowedCustomPaths = ['bahela-event', 'bahela']
    const isAllowedCustomPath = customPath && allowedCustomPaths.includes(customPath)

    // Also allow video uploads if the host is app.bahela.id
    const isBahelaHost = window.location.hostname === 'app.bahela.id'

    const userEmail = useAuthStore.getState().user?.email || ''

    const unifiedCredits = useCreditsStore.getState().credits
    const hasEnoughCreditsForVideo =
      typeof unifiedCredits === 'number' && unifiedCredits >= 2

    if (
      (type === 'video' || type === 'recording') &&
      !isAllowedCustomPath &&
      !isBahelaHost &&
      !hasEnoughCreditsForVideo &&
      !['protrial', 'professional', 'enterprise'].includes(subscriptionType)
    ) {
      const errorMsg =
        'Video upload requires at least 2 credits or Professional/Enterprise package.'
      useSessionStateStore.getState().logActivity(`Upload task creation failed: ${errorMsg}`, {
        type,
        fileName: file.name,
        subscriptionType,
        customPath,
        isAllowedCustomPath,
        isBahelaHost,
        hasEnoughCreditsForVideo,
        unifiedCredits,
        userEmail,
        level: 'error'
      })
      cogoToast.error(errorMsg)
      return {
        taskId: '',
        promise: Promise.reject(new Error(errorMsg))
      }
    }

    // Add random suffix to prevent collision when multiple files are uploaded simultaneously (e.g. gif + mp4)
    const uniqueSuffix = Math.random().toString(36).substring(2, 9)
    const taskId = `${sessionId}_${Date.now()}_${uniqueSuffix}`

    console.log(`[UPLOAD] Creating task ${taskId} for file: ${file.name} (${type})`)
    
    // Log start of task to activity logs for persistence
    useSessionStateStore.getState().logActivity(
      `Starting upload task: ${file.name}`,
      { taskId, type, size: file.size, sessionId },
      'uploadManager',
      sessionId
    )

    const task: UploadTask = {
      id: taskId,
      file,
      fileData: {
        name: file.name,
        size: file.size,
        type: file.type
      },
      userId,
      projectId,
      sessionId,
      galleryId,
      type,
      eventId,
      status: 'pending',
      retries: 0,
      fallbackFile
    }

    this.tasks.set(taskId, task)
    console.log(`[UPLOAD] Task ${taskId} added to map. Total tasks: ${this.tasks.size}`)

    // Try to save to IndexedDB for persistence, but don't fail the entire task if it fails
    let persistenceSuccess = false
    try {
      await saveFileInDB(taskId, file)
      persistenceSuccess = true
      this.saveTasks()
    } catch (error) {
      const errorMsg = 'Failed to save file for persistence.'
      console.error('Failed to save file for persistent upload:', error)
      useSessionStateStore
        .getState()
        .logActivity(`File persistence failed for task ${taskId}: ${errorMsg}`, {
          taskId,
          fileName: file.name,
          error: error instanceof Error ? error.message : 'Unknown error',
          level: 'error'
        })

      // Instead of failing the task, mark it for direct upload without persistence
      task.status = 'pending'
      task.error = undefined // Clear any error since we're continuing
      this.tasks.set(taskId, task)

      // Still save the task metadata to localStorage for UI tracking
      this.saveTasks()
    }

    const uploadPromise = new Promise<UploadTask['response']>((resolve, reject) => {
      this.processTask(task)
        .then(() => {
          if (task.response) {
            resolve(task.response)
          } else {
            const errorMsg = 'Upload failed - no response received'
            useSessionStateStore
              .getState()
              .logActivity(`Upload task ${taskId} failed: ${errorMsg}`, {
                taskId,
                type,
                fileName: file.name,
                level: 'error',
                hadPersistence: persistenceSuccess
              })
            reject(new Error(errorMsg))
          }
        })
        .catch((error) => {
          useSessionStateStore
            .getState()
            .logActivity(
              `Upload task ${taskId} promise rejected: ${error instanceof Error ? error.message : 'Unknown error'}`,
              {
                taskId,
                type,
                fileName: file.name,
                error: error instanceof Error ? error.message : 'Unknown error',
                level: 'error',
                hadPersistence: persistenceSuccess
              }
            )
          reject(error)
        })
    })

    return { taskId, promise: uploadPromise }
  }

  private async processTask(task: UploadTask): Promise<void> {
    if (task.status === 'completed' || task.status === 'uploading') {
      return
    }

    // Skip if no file and task has already failed
    if (!task.file && task.status === 'failed') {
      return
    }

    // Validate file
    if (!task.file) {
      const errorMsg = 'No file available for upload'
      useSessionStateStore.getState().logActivity(`Task ${task.id} failed: ${errorMsg}`, {
        taskId: task.id,
        type: task.type,
        level: 'error'
      })
      task.status = 'failed'
      task.error = errorMsg
      this.saveTasks()
      return
    }

    // Check network status
    const isOnline = await checkNetworkStatus()

    // If online, attempt direct upload
    if (isOnline) {
      task.status = 'uploading'
      this.saveTasks()

      try {
        useSessionStateStore.getState().logActivity(`Uploading ${task.type}: ${task.file.name}`, {
          type: task.type,
          size: task.file.size
        })

        // Use hardcod  ed projectId for bahela users without a project (same as gallery update)
        const BAHELA_DEFAULT_PROJECT_ID = '691d305ea5b85a88793907bb'
        const normalizedProjectIdForS3 =
          !task.projectId || task.projectId.trim() === ''
            ? BAHELA_DEFAULT_PROJECT_ID
            : task.projectId

        const response = await S3Utils.uploadFile(
          task.file,
          task.userId,
          normalizedProjectIdForS3,
          task.sessionId,
          task.galleryId,
          task.eventId
        )

        if (task.status !== 'uploading') {
          console.log(
            `[UPLOAD] Task ${task.id} status changed during upload (${task.status}), skipping duplicate processing`
          )
          return
        }

        task.response = response
        task.status = 'completed'
        task.error = undefined
        this.saveTasks() // Save immediately to prevent duplicate processing

        // Mark file as uploaded for deduplication
        await markFileAsUploaded(task.sessionId, task.file)

        // Update Gallery in MongoDB - wrap in try-catch to prevent gallery update failure from failing the upload
        try {
        await this.updateGallery(task)
        } catch (galleryError) {
          const galleryErrorMsg =
            galleryError instanceof Error ? galleryError.message : 'Unknown error'
          console.error(
            `Gallery update failed for task ${task.id}, but upload succeeded:`,
            galleryError
          )
          useSessionStateStore.getState().logActivity(
            `Gallery update failed for task ${task.id}, but S3 upload succeeded`,
            {
              taskId: task.id,
              type: task.type,
              fileName: task.file.name,
              sessionId: task.sessionId,
              error: galleryErrorMsg,
              level: 'error'
            },
            undefined,
            task.sessionId
          )
          // Don't throw - upload succeeded, gallery update failed
          // This allows the upload to complete even if gallery update fails
        }

        // After successful remote upload, persist media locally for reprint/checkpoints
        try {
          const { saveMediaOffline } = await import('@renderer/utils/offlineStorage')

          let fileType: 'photos' | 'videos' | 'gifs' = 'photos'
          if (task.type === 'gif') {
            fileType = 'gifs'
          } else if (task.type === 'recording' || task.type === 'video' || task.type === 'live') {
            fileType = 'videos'
          }

          let dimensions: { width: number; height: number } | null = null
          try {
            dimensions = await S3Utils.getImageDimensions(task.file)
          } catch (e) {
            console.warn('[uploadManager] Failed to get image dimensions for local save:', e)
          }

          const metadata: {
            mimeType?: string
            width?: number
            height?: number
            alreadyUploaded?: boolean
          } = {
            mimeType: task.file.type,
            alreadyUploaded: true
          }

          if (dimensions) {
            metadata.width = dimensions.width
            metadata.height = dimensions.height
          }

          const localSaveResult = await saveMediaOffline({
            sessionId: task.sessionId,
            fileName: task.file.name,
            file: task.file,
            fileType,
            metadata
          })

          if (!localSaveResult.success) {
            console.warn(
              '[uploadManager] Failed to persist uploaded media locally:',
              localSaveResult.error
            )
          }
        } catch (localError) {
          console.warn('[uploadManager] Error during local persistence after upload:', localError)
        }

        await deleteFileFromDB(task.id).catch((e) => {
          console.error('Error deleting file from IndexedDB:', e)
          useSessionStateStore
            .getState()
            .logActivity(`Failed to delete file from IndexedDB for task ${task.id}`, {
              taskId: task.id,
              error: e instanceof Error ? e.message : 'Unknown error',
              level: 'warning'
            })
        })

        // Log completion only once - task.status is now 'completed' so this won't run again
        // Log completion via IPC to Main process (which updates DB and adds Activity Log)
        try {
          // Check if we have the API available (Bridge)
          const bridge = window as typeof window & { api?: ElectronBridge }
          if (bridge.api?.markMediaAsUploaded) {
            // Main process generates resourceId by concatenating sessionId and fileName (without extension)
            // We must match this exactly so the SQL query finds the correct record
            const derivedResourceId = `${task.sessionId}_${task.file.name.replace(/\.[^/.]+$/, '')}`
            
            await bridge.api.markMediaAsUploaded(
              task.sessionId,
              derivedResourceId, // Use the derived ID that matches the main process database
              task.type as 'photo' | 'video' | 'gif',
              task.file.name
            )
            console.log(`[UPLOAD] Marked task ${task.id} (via resourceId: ${derivedResourceId}) as uploaded via IPC`)
          } else {
             // Fallback for web/dev without IPC - keep local logging
             useSessionStateStore.getState().logActivity(
              `Upload task ${task.id} completed successfully (IPC unavailable)`,
              {
                taskId: task.id,
                type: task.type,
                fileName: task.file.name,
                s3Url: response.url,
                sessionId: task.sessionId,
                galleryId: task.galleryId
              },
              undefined,
              task.sessionId
            )
          }
        } catch (ipcError) {
          console.error('[UPLOAD] Failed to mark media as uploaded via IPC:', ipcError)
        }
        return
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Unknown error'
        console.error('Error uploading file:', error)
        useSessionStateStore.getState().logActivity(
          `Upload failed for ${task.file.name}`,
          {
            type: task.type,
            error: errorMsg,
            level: 'error',
            taskId: task.id,
            userId: task.userId,
            projectId: task.projectId,
            sessionId: task.sessionId,
            galleryId: task.galleryId
          },
          undefined,
          task.sessionId
        )

        // If S3 upload already succeeded, don't retry to avoid duplicate uploads
        if (task.response) {
          console.warn(
            `S3 upload succeeded but error occurred for task ${task.id}. Not retrying to avoid duplicates.`
          )
          task.status = 'completed' // Keep completed status since S3 succeeded
          this.saveTasks()
          return
        }

        // Check for GIF fallback if video upload fails and retries are exhausted (or early exit if preferred strategies change)
        // Here we try standard retries first, then fallback.
        if (task.retries < this.maxRetries) {
          task.retries++
          task.status = 'pending'
          task.error = errorMsg
          this.saveTasks()
          // Exponential backoff: 1s, 2s, 4s, up to 30s
          useSessionStateStore.getState().logActivity(
            `Retrying task ${task.id} (attempt ${task.retries}/${this.maxRetries})`,
            {
              taskId: task.id,
              type: task.type,
              fileName: task.file.name,
              retryCount: task.retries,
              maxRetries: this.maxRetries,
              error: errorMsg
            },
            undefined,
            task.sessionId
          )
          // Exponential backoff: 1s, 2s, 4s
          const delay = Math.min(1000 * Math.pow(2, task.retries), 30000)
          setTimeout(() => this.processTask(task), delay)
          this.saveTasks()
          return
        } else if (
          task.type === 'video' &&
          task.fallbackFile &&
          !task.hasRetriedFallback
        ) {
          // Fallback to GIF if video fails
          const msg = `[UploadManager] Video upload failed after retries, falling back to GIF for task ${task.id}`
          console.warn(msg)
          
          useSessionStateStore.getState().logActivity(
            msg,
            {
              taskId: task.id,
              originalType: task.type,
              newType: 'gif',
              fileName: task.fallbackFile.name
            },
            undefined,
            task.sessionId
          )

          // Update task to use fallback file
          task.file = task.fallbackFile
          task.type = 'gif'
          task.hasRetriedFallback = true
          task.retries = 0 // Reset retries for the new file
          task.error = undefined
          task.status = 'pending'
          
          this.saveTasks()
          // Retry immediately or with small delay
          setTimeout(() => this.processTask(task), 1000)
          return
        }

        // After max retries, re-check network status
        const stillOnline = await checkNetworkStatus()
        if (stillOnline) {
          // Still online but upload failed after retries - mark as failed
          task.status = 'failed'
          task.error = errorMsg
          useSessionStateStore.getState().logActivity(
            `Task ${task.id} failed permanently after ${this.maxRetries} retries (still online)`,
            {
              taskId: task.id,
              type: task.type,
              fileName: task.file.name,
              finalError: errorMsg,
              retryCount: task.retries,
              level: 'error'
            },
            undefined,
            task.sessionId
          )
          await deleteFileFromDB(task.id).catch((e) => {
            console.error('Error deleting failed task file from IndexedDB:', e)
            useSessionStateStore
              .getState()
              .logActivity(`Failed to delete failed task file from IndexedDB for task ${task.id}`, {
                taskId: task.id,
                error: e instanceof Error ? e.message : 'Unknown error',
                level: 'warning'
              })
          })
          this.saveTasks()
          return
        }

        // Network went offline during retries - fall through to offline path
        useSessionStateStore.getState().logActivity(
          `Network went offline during retries for task ${task.id}, switching to offline path`,
          {
            taskId: task.id,
            type: task.type,
            fileName: task.file.name,
            level: 'info'
          },
          undefined,
          task.sessionId
        )
        // Reset retries for offline retry mechanism
        task.retries = 0
      }
    }

    // Offline path: Check if preload API is available
    // Note: If task was created when offline with preload API, addTask would have already
    // saved it and returned early, so processTask would never be called for that case.
    // This path is only for tasks that were created online but network went offline during retries.
    const hasPreload = hasPreloadAPI()

    if (hasPreload) {
      // This handles the case where task was created online but network went offline during retries
      try {
        useSessionStateStore.getState().logActivity(
          `Handing off task ${task.id} to offline pipeline (network went offline during retries)`,
          {
            taskId: task.id,
            type: task.type,
            fileName: task.file.name
          },
          undefined,
          task.sessionId
        )

        const { saveMediaOffline } = await import('@renderer/utils/offlineStorage')

        // Determine file type for offline storage
        let fileType: 'photos' | 'videos' | 'gifs' = 'photos'
        if (task.type === 'gif') {
          fileType = 'gifs'
        } else if (task.type === 'recording' || task.type === 'video' || task.type === 'live') {
          fileType = 'videos'
        }

        // Calculate dimensions for metadata
        let dimensions: { width: number; height: number } | null = null
        try {
          dimensions = await S3Utils.getImageDimensions(task.file)
        } catch (e) {
          console.warn('[uploadManager] Failed to get image dimensions for handoff:', e)
        }

        const metadata: { mimeType: string; width?: number; height?: number } = { mimeType: task.file.type }
        if (dimensions) {
          metadata.width = dimensions.width
          metadata.height = dimensions.height
        }

        const result = await saveMediaOffline({
          sessionId: task.sessionId,
          fileName: task.file.name,
          file: task.file,
          fileType: fileType,
          metadata
        })

        if (result.success) {
          // Offline pipeline will handle upload when online
          // Mark file as uploaded to prevent duplicate uploads
          // Note: We mark it here even though upload happens later, to prevent duplicate addTask calls
          await markFileAsUploaded(task.sessionId, task.file)

          task.status = 'completed'
          task.error = undefined
          // Note: We don't have a response URL yet, but the offline pipeline will handle it
          // For now, resolve with null - the gallery update will happen when sync completes
          task.response = null as unknown as UploadTask['response']
          this.saveTasks()

          await deleteFileFromDB(task.id).catch((e) => {
            console.error('Error deleting file from IndexedDB:', e)
          })

          useSessionStateStore.getState().logActivity(
            `Task ${task.id} handed off to offline pipeline`,
            {
              taskId: task.id,
              type: task.type,
              fileName: task.file.name,
              resourceId: result.resourceId
            },
            undefined,
            task.sessionId
          )
          return
        } else {
          throw new Error(result.error || 'Failed to save to offline pipeline')
        }
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Unknown error'
        useSessionStateStore.getState().logActivity(
          `Offline pipeline handoff failed for task ${task.id}, using IndexedDB retry`,
          {
            taskId: task.id,
            type: task.type,
            fileName: task.file.name,
            error: errorMsg,
            level: 'warning'
          },
          undefined,
          task.sessionId
        )
        // Fall through to IndexedDB retry mechanism
      }
    }

    // Preload API not available (version mismatch) - use IndexedDB retry mechanism
    // Ensure file is saved in IndexedDB for retry
    try {
      await saveFileInDB(task.id, task.file)
    } catch (error) {
      useSessionStateStore.getState().logActivity(
        `Failed to save task ${task.id} to IndexedDB for retry`,
        {
          taskId: task.id,
          type: task.type,
          fileName: task.file.name,
          error: error instanceof Error ? error.message : 'Unknown error',
          level: 'error'
        },
        undefined,
        task.sessionId
      )
    }

    // Schedule retry every 3 minutes (180000ms)
    const RETRY_INTERVAL_MS = 180000
    task.status = 'pending'
    task.error = 'Offline - will retry when online'

    useSessionStateStore.getState().logActivity(
      `Task ${task.id} scheduled for retry in 3 minutes (preload API unavailable)`,
      {
        taskId: task.id,
        type: task.type,
        fileName: task.file.name,
        retryInterval: RETRY_INTERVAL_MS
      },
      undefined,
      task.sessionId
    )

    setTimeout(async () => {
      // Re-check network status before retry
      const isOnlineNow = await checkNetworkStatus()
      if (isOnlineNow) {
        // Network is back - try direct upload
        await this.processTask(task)
      } else {
        // Still offline - schedule another retry
        setTimeout(() => this.processTask(task), RETRY_INTERVAL_MS)
      }
    }, RETRY_INTERVAL_MS)

    this.saveTasks()
  }

  private async updateGallery(task: UploadTask): Promise<void> {
    if (!task.response) {
      useSessionStateStore
        .getState()
        .logActivity(`Cannot update gallery for task ${task.id}: no response available`, {
          taskId: task.id,
          type: task.type,
          level: 'error'
        })
      return
    }

    const subscriptionType = useSubscriptionStore.getState().type
    const expiresAt = calculateExpirationDate(subscriptionType)

    const mediaItem = {
      key: task.response.key,
      url: task.response.url,
      thumbnailUrl: task.response.thumbnailUrl,
      type: task.type,
      createdAt: new Date(),
      expiresAt,
      metadata: {
        originalFilename: task.response.metadata.originalFilename,
        contentType: task.response.metadata.contentType,
        size: Number(task.response.metadata.size),
        ...(task.response.metadata.width && {
          width: Number(task.response.metadata.width),
          height: Number(task.response.metadata.height)
        })
      }
    }

    try {
      const galleryUrl = resolveGallerySessionUrlFromStore(task.sessionId)

      // First, try to get existing gallery to ensure we have the latest state
      const getGalleryUrl = `${process.env.VITE_API_URL}/api/gallery?sessionId=${task.sessionId}`

      await fetch(getGalleryUrl, {
        method: 'GET',
        credentials: 'include'
      })

      // Now create/update the gallery
      const createGalleryUrl = `${process.env.VITE_API_URL}/api/gallery`
      // Gallery schema requires projectId to be a non-empty string (required: true)
      // Use hardcoded projectId for bahela users without a project
      const BAHELA_DEFAULT_PROJECT_ID = '691d305ea5b85a88793907bb'
      const normalizedProjectId =
        !task.projectId || task.projectId.trim() === ''
          ? BAHELA_DEFAULT_PROJECT_ID
          : task.projectId
      const { useSettingsStore } = await import('@renderer/stores/settingsStore')
      const requestBody = {
        sessionId: task.sessionId,
        userId: task.userId,
        projectId: normalizedProjectId,
        eventId: task.eventId,
        mediaItem,
        url: galleryUrl,
        token: useSettingsStore.getState().activationCode || undefined
      }

      const response = await fetch(createGalleryUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      })

      if (!response.ok) {
        const errorText = await response.text()
        const errorMsg = `Failed to update gallery: ${response.status} ${response.statusText}`
        console.error(`[GALLERY UPDATE FAILED] Task ${task.id}:`, {
          sessionId: task.sessionId,
          projectId: normalizedProjectId,
          userId: task.userId,
          status: response.status,
          statusText: response.statusText,
          errorText,
          requestBody: {
            sessionId: task.sessionId,
            userId: task.userId,
            projectId: normalizedProjectId,
            eventId: task.eventId,
            mediaItem: {
              key: mediaItem.key,
              type: mediaItem.type,
              url: mediaItem.url
            }
          }
        })
        useSessionStateStore
          .getState()
          .logActivity(`Gallery update failed for task ${task.id}: ${errorMsg}`, {
            taskId: task.id,
            type: task.type,
            sessionId: task.sessionId,
            projectId: normalizedProjectId,
            userId: task.userId,
            responseStatus: response.status,
            responseStatusText: response.statusText,
            responseText: errorText,
            level: 'error'
          })
        throw new Error(`${errorMsg}: ${errorText}`)
      }

      await response.json()

      // Gallery updated - stats are tracked in Gallery model, not Session
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error'
      console.error('Error updating gallery:', error)
      useSessionStateStore
        .getState()
        .logActivity(`Gallery update error for task ${task.id}: ${errorMsg}`, {
          taskId: task.id,
          type: task.type,
          sessionId: task.sessionId,
          galleryId: task.galleryId,
          error: errorMsg,
          level: 'error',
          stack: error instanceof Error ? error.stack : undefined
        })
      throw error // Re-throw to handle in the upload process
    }
  }

  isSessionComplete(sessionId: string): boolean {
    const sessionTasks = Array.from(this.tasks.values()).filter(
      (task) => task.sessionId === sessionId
    )

    // Consider a session complete if all tasks are either completed or failed
    return sessionTasks.every((task) => task.status === 'completed' || task.status === 'failed')
  }

  getSessionTasks(sessionId: string): UploadTask[] {
    const sessionTasks = Array.from(this.tasks.values()).filter(
      (task) => task.sessionId === sessionId
    )

    return sessionTasks
  }

  // Add methods for IndexedDB health management
  getDBHealthStatus(): { status: 'healthy' | 'degraded' | 'failed'; failureCount: number } {
    return {
      status: dbHealthStatus,
      failureCount: dbFailureCount
    }
  }

  async attemptDBRecovery(): Promise<boolean> {
    try {
      const isHealthy = await checkDBHealth()
      if (isHealthy) {
        dbHealthStatus = 'healthy'
        dbFailureCount = 0
        return true
      }
      return false
    } catch (error) {
      useSessionStateStore
        .getState()
        .logActivity(
          `IndexedDB recovery failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
          {
            level: 'error'
          }
        )
      return false
    }
  }

  // Method to retry failed tasks with direct upload (when IndexedDB is unavailable)
  async retryFailedTasksWithDirectUpload(): Promise<void> {
    const failedTasks = Array.from(this.tasks.values()).filter(
      (task) => task.status === 'failed' && task.error?.includes('persistent storage')
    )

    if (failedTasks.length === 0) {
      return
    }

    useSessionStateStore
      .getState()
      .logActivity(`Attempting to retry ${failedTasks.length} failed tasks with direct upload`, {
        failedTaskCount: failedTasks.length,
        failedTaskIds: failedTasks.map((t) => t.id),
        dbHealthStatus
      })

    for (const task of failedTasks) {
      if (task.file) {
        // Reset task for retry
        task.status = 'pending'
        task.error = undefined
        task.retries = 0

        // Process the task directly
        this.processTask(task)
      } else {
        useSessionStateStore
          .getState()
          .logActivity(`Cannot retry task ${task.id}: no file available`, {
            taskId: task.id,
            type: task.type,
            level: 'warning'
          })
      }
    }

    this.saveTasks()
  }
}

export const uploadManager = UploadManager.getInstance()
